// components/ProjectModal.tsx
"use client";

import Link from "next/link";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IconArrowRight, IconArrowUpRight, IconBrandWhatsapp, IconStack3, IconX } from "@tabler/icons-react";
import * as React from "react";
import { projectImages, projectStack, splitTitle, type Project } from "@/constants/projects";
import ProjectGallery from "@/components/ProjectGallery";
import { contact } from "@/constants/site";


export interface ProjectModalProps {
  opened: boolean;
  onClose: () => void;
  project: Project | null;
}

export default function ProjectModal({ opened, onClose, project }: ProjectModalProps) {
  if (!project) return null;

  const { name, kind } = splitTitle(project.title);
  const stack = projectStack(project);

  return (
    <Dialog open={opened} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent
        showCloseButton={false}
        className="w-[94vw] max-w-[94vw] gap-0 overflow-hidden rounded-[20px] border border-white/[0.08] bg-[#07080d] p-0 text-white sm:max-w-[92vw] md:w-[88vw] md:max-w-[88vw] lg:w-[80vw] lg:max-w-[80vw] xl:max-w-[1200px]"
      >
        <DialogClose
          aria-label="Close"
          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/75 text-white backdrop-blur transition-colors hover:bg-black"
        >
          <IconX size={18} />
        </DialogClose>
        <ScrollArea className="max-h-[88dvh]">
          <ProjectGallery key={project.id} images={projectImages(project)} />

          <div className="p-4 sm:p-6 md:p-8">
            {project.category && (
              <span className="inline-block rounded-full bg-[#48AEEF]/10 px-2.5 py-1 text-[12px] font-semibold text-[#48AEEF]">
                {project.category}
              </span>
            )}
            <DialogTitle className="mt-3 text-[24px] font-bold leading-tight sm:text-[28px] lg:text-[34px]">
              {name}
            </DialogTitle>
            {kind && <p className="mt-1 text-[16px] font-medium text-[#A5B0BC] lg:text-[18px]">{kind}</p>}
            <DialogDescription className="sr-only">Details and links for {name}.</DialogDescription>

            <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="rounded-[16px] border border-white/[0.06] bg-black p-4 sm:p-5 md:p-6">
                <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#5C6670]">
                  About the project
                </h3>
                <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-[#9AA3AD] lg:text-[16px]">
                  {project.description}
                </p>
              </div>

              <aside className="flex flex-col gap-4">
                <Link
                  href={`/projects/${project.slug}`}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-[12px] border border-[#48AEEF]/30 bg-[#48AEEF]/10 text-[15px] font-bold text-white transition-colors hover:bg-[#48AEEF]/20"
                >
                  Read full case study
                  <IconArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
                <div className="rounded-[16px] border border-white/[0.06] bg-black p-5">
                  <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#5C6670]">
                    <IconStack3 size={16} className="text-[#48AEEF]" />
                    Tech stack
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {stack.map((t) => (
                      <span
                        key={t}
                        className="rounded-[8px] border border-white/[0.06] bg-[#0E1018] px-2.5 py-1.5 text-[13px] font-medium text-[#C9D1D9]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {project.demoUrl && (
                  <Link
                    href={project.demoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-[12px] bg-white text-[15px] font-bold text-black transition-colors hover:bg-white/90"
                  >
                    Visit live project
                    <IconArrowUpRight
                      size={17}
                      className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </Link>
                )}
                <Link
                  href={contact.whatsapp}
                  target="_blank"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-[12px] border border-white/10 bg-[#0E1018] text-[15px] font-bold text-white transition-colors hover:bg-[#161a26]"
                >
                  <IconBrandWhatsapp size={18} />
                  Want something similar?
                </Link>
              </aside>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
